<template>
    <div class="framework">
        <top-nav v-if="currentUser"/>
        <slot name="bar"></slot>
        <div class="framework-body" :style="{'background': colors.background1 + ' radial-gradient(' + colors.background1 + ',' + colors.background2 + ')'}">
            <div class="side-panel" v-if="showSide" :class="{'minimized': sidePanel.minimized, 'details': sidePanel.details, 'is-floating': floating}">
                <slot name="side"></slot>
            </div>
            <!-- Narrow windows: the expanded panel slides over the content; a
                 click on the shaded page behind it collapses it again -->
            <div class="side-backdrop" v-if="overlayOpen" @click="closeOverlay"></div>
            <div class="main-panel" :class="{'no-side': !showSide, 'has-rail-gap': floating}">
                <slot name="main"></slot>
            </div>
        </div>
    </div>
</template>

<script>
    import {mapGetters} from "vuex";
    import TopNav from "./TopNav";
    import {routeShowsSide, NARROW_BREAKPOINT} from "../../constants/navigation";

    export default {
        name: "Framework",
        components: {TopNav},
        data() {
            return {
                narrow: window.innerWidth < NARROW_BREAKPOINT
            };
        },
        computed: {
            ...mapGetters(['currentUser', 'sidePanel', 'colors']),
            // On narrow windows the panel is always out of the flow (rail or
            // expanded), so opening/closing only changes its width over the
            // page and the content never reflows
            floating() {
                return this.showSide && this.narrow;
            },
            overlayOpen() {
                return this.floating && !this.sidePanel.minimized;
            },
            // The left panel is the section submenu. Routes with nothing to put
            // there (dashboard, support) opt out via meta.side = false.
            showSide() {
                const hasSlot = !!(this.$scopedSlots.side || this.$slots.side);
                return hasSlot && routeShowsSide(this.$route);
            }
        },
        methods: {
            onResize() {
                this.narrow = window.innerWidth < NARROW_BREAKPOINT;
            },
            closeOverlay() {
                this.$store.commit('setMinimized', true);
            }
        },
        watch: {
            // Picking a destination from the slide-over closes it
            '$route'() {
                if (this.narrow) this.$store.commit('setMinimized', true);
            }
        },
        mounted() {
            window.addEventListener('resize', this.onResize);
        },
        beforeDestroy() {
            window.removeEventListener('resize', this.onResize);
        }
    };
</script>

<style lang="scss" scoped>
    .framework {
        display: flex;
        flex-direction: column;
        height: 100%;
        overflow: hidden;

        // The blue ground lives here (inline style carries the theme colors)
        // so it shows above and below the side card. Depth from the bars
        // above falls onto it across the full width.
        .framework-body {
            position: relative;
            display: flex;
            flex-direction: row;
            flex: 1;
            min-height: 0;
            background: #224C74 radial-gradient(#336699, #213f6a);
            box-shadow: inset 0 12px 18px -12px rgba(0, 0, 0, 0.35);
        }

        // Side card: attached to the left edge, floating on the blue with a
        // gap above and below and rounded outer corners
        $card-gap: 1.75rem;
        $card-gap-phone: 1rem;
        $card-radius: 14px;

        // Narrow window: the panel floats over the page (rail or expanded)
        .side-panel.is-floating {
            position: absolute;
            top: $card-gap;
            bottom: $card-gap;
            left: 0;
            margin: 0;

            @media (max-width: 768px) {
                top: $card-gap-phone;
                bottom: $card-gap-phone;
            }
            z-index: 120;
            transition: width 150ms ease-out;

            &:not(.minimized) {
                box-shadow: 8px 8px 40px 0 rgba(0, 0, 0, 0.45);
            }
        }

        .side-backdrop {
            position: absolute;
            inset: 0;
            z-index: 115;
            background: rgba(20, 35, 55, 0.45);
        }

        // Keep the rail's footprint so the page doesn't shift while the
        // panel is over it
        .main-panel.has-rail-gap {
            margin-left: 63px;

            @media (max-width: 768px) {
                margin-left: 49px;
            }
        }

        .side-panel {
            position: relative;
            width: 280px;
            min-width: 280px;
            margin: $card-gap 0;
            border-radius: 0 $card-radius $card-radius 0;
            overflow: hidden; // clips the menu/controls to the rounded corners
            transition: width 100ms;
            box-shadow: 5px 6px 30px 0 rgba(0, 0, 0, 0.35);
            z-index: 100;

            @media (max-width: 768px) {
                margin: $card-gap-phone 0;
                transition: none;
            }

            &.minimized {
                width: 63px !important;
                min-width: 63px !important;
                box-shadow: 3px 6px 30px 0 rgba(0, 0, 0, 0.35);

                @media (max-width: 768px) {
                    width: 49px !important;
                    min-width: 49px !important;
                }
            }

            &.details {
                width: 360px;
                min-width: 360px;
            }
        }

        .main-panel {
            overflow-y: auto;
            position: relative;
            flex-grow: 1;
            min-width: 0;

            padding: 1.5rem 4.5rem 0 5rem;

            @media (max-width: 1180px) {
                padding: 0 3rem 1.5rem 45px;
            }

            @media (max-width: 850px) {
                padding: 0 2rem 1.5rem 45px;
            }

            @media (max-width: 768px) {
                padding: 0 1.5rem 1.5rem 1.5rem;
            }
        }
    }
</style>
